import { authApi } from "@api";
import { envConfig } from "@configs/env";
import { SEON_FEATURE } from "@configs/seon";
import { SEARCH_PARAMS } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import { useSeonSession } from "@hooks/useSeonSession";
import { AppDispatch } from "@stores";
import auth from "@stores/auth";
import { AuthStatus } from "@types";
import logger from "@utils/logger";
import { FastLoginRequest, LoginGrantType } from "kz-ui-sdk";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

/**
 * Component that handles fast login token exchange from notification URLs.
 * Automatically exchanges fastLoginToken query parameter for an access token.
 */
const FastLoginHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { status } = useAuthSession();
  const dispatch = useDispatch<AppDispatch>();
  const [triggerLogin] = authApi.useLoginMutation();
  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.LOGIN);
  const processedRef = useRef(false);

  useEffect(() => {
    const fastLoginToken = searchParams.get(SEARCH_PARAMS.FAST_LOGIN_TOKEN);

    // No token in URL, skip processing
    if (!fastLoginToken) {
      return;
    }

    logger._console.log("[FastLogin] Token detected in URL", { status, processed: processedRef.current });

    // Skip if user is already authenticated
    if (status === AuthStatus.Authenticated) {
      logger._console.log("[FastLogin] User already authenticated, cleaning URL");
      // Clean URL and mark as processed
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(SEARCH_PARAMS.FAST_LOGIN_TOKEN);
      setSearchParams(newSearchParams, { replace: true });
      processedRef.current = true;
      return;
    }

    // Skip if still authenticating (waiting for initial auth check)
    if (status === AuthStatus.Authenticating) {
      logger._console.log("[FastLogin] Still authenticating, waiting...");
      return;
    }

    // Prevent re-processing if already processed (unless it was a network error)
    if (processedRef.current) {
      logger._console.log("[FastLogin] Already processed, skipping");
      return;
    }

    // Mark as processed to prevent re-processing
    processedRef.current = true;
    logger._console.log("[FastLogin] Starting token exchange");

    // Exchange token for access token
    const exchangeToken = async () => {
      try {
        const fastLoginRequest: FastLoginRequest = {
          fastLoginToken,
          grant_type: LoginGrantType.FastLogin,
          account: envConfig.accountId,
        };

        // Get SEON session for fraud detection (non-blocking)
        logger._console.log("[FastLogin] Fetching SEON session");
        const seonSession = await getSeonSession();
        logger._console.log("[FastLogin] SEON session", { hasSession: !!seonSession });

        logger._console.log("[FastLogin] Calling login API", { grant_type: fastLoginRequest.grant_type });
        const response = await triggerLogin({
          ...fastLoginRequest,
          seonSession,
        });

        // Check if response has error
        if (response.error) {
          // Check if it's a network error
          const isNetworkError = "status" in response.error && response.error.status === "FETCH_ERROR";

          if (isNetworkError) {
            logger._console.error("[FastLogin] Network error, keeping URL for retry", response.error);
            // Network error: keep URL for retry, reset processed flag
            processedRef.current = false;
            return;
          }

          logger._console.error("[FastLogin] API error, cleaning URL", {
            status: "status" in response.error ? response.error.status : "unknown",
            error: response.error,
          });
          // Invalid/expired token or other API error: clean URL silently
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete(SEARCH_PARAMS.FAST_LOGIN_TOKEN);
          setSearchParams(newSearchParams, { replace: true });
          return;
        }

        // Success: store token and clean URL
        if (response.data) {
          logger._console.log("[FastLogin] Token exchange successful, storing token");
          dispatch(auth.slice.actions.resetExpiredAt());
          dispatch(auth.slice.actions.updateAccessToken(response.data));

          // Clean URL after successful token storage
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete(SEARCH_PARAMS.FAST_LOGIN_TOKEN);
          setSearchParams(newSearchParams, { replace: true });
          logger._console.log("[FastLogin] Token stored and URL cleaned");
        }
      } catch (error) {
        logger._console.error("[FastLogin] Unexpected error, keeping URL for retry", error);
        // Unexpected error: treat as network error, keep URL for retry
        processedRef.current = false;
      }
    };

    exchangeToken();
  }, [searchParams, setSearchParams, status, triggerLogin, dispatch, getSeonSession]);

  return null;
};

export default FastLoginHandler;
